import React, { Fragment } from "react";
import { styled } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  Container,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  Divider,
  accordionSummaryClasses,
} from "@mui/material";
import { Link } from "react-router-dom";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * PM5 T2 (money path, batch "freetrial-buynow") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **PURCHASE-FUNNEL SURFACE — RENDERS PRICES.** The doors-open /buy landing
 * FAQ, including the hard-coded Annual/Monthly price panels (£79.00/€95.00/
 * $99.00 and £9.99/€11.99/$12.99). Every one of those figures, and every FAQ
 * `id`/`title`/`body`, is byte-identical to the v4 file — this conversion moves
 * styling only.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `title`, `accordionContainer` and
 * `membershipOptionWrapper` were declared in the v4 `useStyles` object but
 * never referenced from the JSX.
 */

// was: useStyles().background — a bare <div>, which has no `sx` prop.
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavy,
  padding: theme.spacing(6, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(8, 0),
  },
}));

/**
 * ♦ v4's `<Grid container spacing={2}>` — ported verbatim rather than swapped
 * for v9's `gap` (guide §2, "Grid `spacing` implementation" + its T2/signin
 * refinement): `spacing` omitted (v9 default 0 ⇒ `gap: 0px`), the container
 * restates the negative margin and over-wide width, each item carries the 8px
 * padding. Identical by construction. Used for BOTH containers in this file —
 * the page-level one and the price-panel one inside the membership FAQ.
 */
const v4SpacingContainerSx = {
  width: "calc(100% + 16px)",
  margin: "-8px",
};
const v4GridItemSx = { padding: "8px" };

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "5.4rem",
    lineHeight: "5rem",
  },
});

/**
 * was: useStyles().accordionEntry
 *
 * ♦ §1.2b HIT — the v4 rule reached into its child with the hard-coded global
 * selector `& .MuiAccordionSummary-expandIcon`. Both the `FfV5` class prefix
 * AND the slot name move now that this file converts: v5+ renamed
 * `expandIcon` → `expandIconWrapper`, so re-prefixing alone would match
 * nothing. The name is read off `accordionSummaryClasses` so it tracks both.
 *
 * MAPPING NOTE — v4 rendered the expand icon inside an `IconButton`
 * (`padding: 12`) which this rule overrode to `0px 12px`; v9 renders it inside
 * a plain `<div>` with no padding of its own, so the same declaration yields
 * the same box. The IconButton's circular hover/ripple wash does not carry
 * over — that is a v5+ library default of `AccordionSummary`, not this rule.
 */
const accordionEntrySx = (theme: Theme) => ({
  backgroundColor: "background.ffNavyLighter",
  borderRadius: "0 !important",
  padding: "4px 0px",
  marginBottom: "8px",
  [theme.breakpoints.up("sm")]: {
    padding: "8px 0px",
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    padding: "0px 12px",
  },
});

// was: useStyles().accordionEntryTitle
const accordionEntryTitleSx = (theme: Theme) => ({
  fontSize: "1.1rem",
  lineHeight: "1.2rem",
  fontWeight: 500,
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.3rem",
    lineHeight: "1.5rem",
  },
});

// was: useStyles().accordionEntryText
const accordionEntryTextSx = {
  fontSize: "1rem",
  lineHeight: "1.2rem",
  "& a": {
    color: "#eee",
  },
};

// was: useStyles().accordionEntryExpand
// `fill` has no entry in sx's `defaultSxConfig`, so a palette *path* string
// would be emitted raw as the CSS value (guide §1.2 row 4). Read off the theme.
const accordionEntryExpandSx = (theme: Theme) => ({
  fill: theme.palette.background.ffGreen,
  fontSize: "2.2rem",
  padding: 0,
});

// was: useStyles().membershipOptionContainer
const membershipOptionContainerSx = (theme: Theme) => ({
  textAlign: "center",
  padding: "24px 12px",
  backgroundColor: "background.ffNavyLighter",
  [theme.breakpoints.up("sm")]: {
    padding: "24px 32px",
  },
});

// was: useStyles().membershipOptionTitle
const membershipOptionTitleSx = {
  color: "background.ffGreen",
  fontSize: "1.5rem",
  marginBottom: "24px",
};

// was: useStyles().membershipOptionPrimaryPrice
const membershipOptionPrimaryPriceSx = (theme: Theme) => ({
  fontSize: "2rem",
  color: "common.white",
  fontWeight: 500,
  marginBottom: "24px",
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.6rem",
  },
  "& span": {
    fontSize: "0.9rem",
    fontWeight: "400",
  },
});

// was: useStyles().membershipOptionDescription
const membershipOptionDescriptionSx = {
  margin: "16px 0px",
};

// was: useStyles().smlDivider — themeV5's `MuiDivider.styleOverrides.root`
// already carries `borderBottomWidth: 0` (the v5+ spelling of v4's
// `border: none`), so repainting the bar here leaves no v5+ hairline under it.
const smlDividerSx = (theme: Theme) => ({
  height: "2px",
  width: "30%",
  backgroundColor: "background.ffGreen",
  marginTop: "8px",
  marginBottom: "8px",
  marginLeft: "auto",
  marginRight: "auto",
  [theme.breakpoints.up("sm")]: {
    marginTop: "16px",
    marginBottom: "16px",
  },
});

const FAQs = () => {
  const faqs = [
    {
      id: 1,
      title: "What is FarmFLiX?",
      body: "FarmFLiX is an online video platform dedicated to delivering relevant agricultural content. We bring the reality of the job to you.",
    },
    {
      id: 3,
      title: "How much is FarmFLiX Membership?",
      body: (
        <Fragment>
          <Grid container sx={v4SpacingContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
              <Typography style={{ width: "100%" }} sx={accordionEntryTextSx}>
                FarmFLiX offers two Membership options; Annual and Monthly.
                <br /> <br />
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }} sx={v4GridItemSx}>
              <Card sx={membershipOptionContainerSx}>
                <Typography sx={membershipOptionTitleSx}>Annual</Typography>
                <Typography sx={membershipOptionPrimaryPriceSx}>
                  &pound;79.00<span>/year</span>
                </Typography>
                <Divider sx={smlDividerSx} />
                <Typography sx={membershipOptionDescriptionSx} variant="body2">
                  Our Annual Membership
                </Typography>
                <Typography style={{ margin: "12px 0" }} variant="body2">
                  or
                </Typography>
                <Typography sx={membershipOptionDescriptionSx} variant="body1">
                  <b>&euro;95.00</b> / <b>&#36;99.00</b>
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 6 }} sx={v4GridItemSx}>
              <Card sx={membershipOptionContainerSx}>
                <Typography sx={membershipOptionTitleSx}>Monthly</Typography>
                <Typography sx={membershipOptionPrimaryPriceSx}>
                  &pound;9.99<span>/month</span>
                </Typography>
                <Divider sx={smlDividerSx} />
                <Typography sx={membershipOptionDescriptionSx} variant="body2">
                  Our Monthly Membership
                </Typography>
                <Typography style={{ margin: "12px 0" }} variant="body2">
                  or
                </Typography>
                <Typography sx={membershipOptionDescriptionSx} variant="body1">
                  <b>&euro;11.99</b> / <b>&#36;12.99</b>
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Fragment>
      ),
    },
    {
      id: 7,
      title: "Can I buy FarmFLiX Membership as a gift?",
      body: (
        <Fragment>
          Yes, you can buy a 3 Month or 12 Month Digital Pass from the&nbsp;
          <a
            href="http://shop.farmflix.tv"
            rel="noopener noreferrer"
            target="_blank"
          >
            FarmFLiX Shop
          </a>
          . Digital Passes are sent instantly to the purchaser&apos;s email
          address on successful payment.
        </Fragment>
      ),
      open: 1,
    },
    {
      id: 8,
      title: "How can I watch FarmFLiX on my TV?",
      body: "Install the FarmFLiX App, which supports Google Chromecast and Apple AirPlay.",
      open: 2,
    },
    {
      id: 12,
      title: "How do I redeem a Giftcard?",
      body: (
        <Fragment>
          Simple. You can redeem a giftcard&nbsp;
          <Link to="/usemycode">here</Link>.
        </Fragment>
      ),
      open: 2,
    },
    {
      id: 13,
      title: "Is FarmFLiX suitable for kids?",
      body: "Absolutely.  All our content is family-friendly, showcasing the events of real-world farming.",
      open: 2,
    },
  ];

  return (
    <>
      <Background>
        <Container maxWidth="md" fixed>
          <Grid container sx={v4SpacingContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
              <Typography variant="h2" sx={titleLargeSx}>
                FAQ&apos;s
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
              {faqs.map((q, i) => (
                <Accordion sx={accordionEntrySx} key={i}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={accordionEntryExpandSx} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography sx={accordionEntryTitleSx}>
                      {q.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography sx={accordionEntryTextSx}>{q.body}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default FAQs;
